/**
 * Article data loading and manipulation utilities.
 *
 * Evidence-tier color mapping (v3.0, from brand guide): four tiers with the
 * reserved hues #16A34A / #3B82F6 / #F59E0B / #9CA3AF. These MUST NOT be
 * repurposed for general UI — they signal tier strength.
 *
 * Article kind: high-level editorial categories plus supplement-depth axes.
 * Display labels mirror the browse/article UI vocabulary.
 */

import type { PublicArticle, EvidenceTier, ArticleKind } from './types';

/**
 * Greek letters + a few symbols → ASCII words, applied BEFORE slugification so
 * `PGC-1α` → `pgc-1alpha` and `NF-κB` → `nf-kappab` (matching live canonical
 * slugs) rather than dropping the glyph. Mirrors AviadoVizCLI publish.py
 * `canonical_slug`'s transliteration step. Keep in sync via the shared fixture
 * `lib/aviado/slug-contract-fixtures.json`.
 */
const SLUG_TRANSLITERATIONS: Record<string, string> = {
  α: 'alpha', β: 'beta', γ: 'gamma', δ: 'delta', ε: 'epsilon', ζ: 'zeta',
  η: 'eta', θ: 'theta', ι: 'iota', κ: 'kappa', λ: 'lambda', μ: 'mu',
  ν: 'nu', ξ: 'xi', ο: 'omicron', π: 'pi', ρ: 'rho', σ: 'sigma', ς: 'sigma',
  τ: 'tau', υ: 'upsilon', φ: 'phi', χ: 'chi', ψ: 'psi', ω: 'omega',
  µ: 'mc', // U+00B5 micro sign -> 'mc' (units render mcg, e.g. ug -> mcg); Greek mu U+03BC (above) stays 'mu'
};

function transliterateForSlug(lowercased: string): string {
  let out = '';
  for (const ch of lowercased) out += SLUG_TRANSLITERATIONS[ch] ?? ch;
  return out;
}

/**
 * Generate URL-safe slug from title — the JS reference for the canonical slug
 * contract.
 *
 * IMPORTANT: the Supabase pipeline (AviadoVizCLI `publish.py` `canonical_slug`)
 * OWNS canonical slug generation for live articles; this helper is the
 * client-side fallback / reference implementation. It is intentionally kept in
 * lock-step with that contract so the two never diverge:
 *   1. lowercase
 *   2. transliterate Greek/symbol glyphs → ASCII words (α→alpha, κ→kappa)
 *   3. remove apostrophes/possessives entirely (`curcumin's` → `curcumins`)
 *   4. every other run of non-[a-z0-9] (incl. EVERY dash — em/en/figure) → one `-`
 *   5. trim hyphens; 120-char word-boundary ceiling (never mid-word)
 *
 * Verified against `lib/aviado/slug-contract-fixtures.json` by
 * `scripts/check-slug-contract.ts` (runs in `prebuild`). See
 * `docs/aviadovizcli-slug-contract-prompt.md`.
 */
export function generateSlug(title: string): string {
  const raw = transliterateForSlug(title.toLowerCase())
    .replace(/['’`´]/g, '')          // possessives → removed (no hyphen)
    .replace(/[^a-z0-9]+/g, '-')     // everything else (incl. all dashes) → single hyphen
    .replace(/^-+|-+$/g, '');
  if (raw.length <= 120) return raw;
  // Word-boundary trim: cut at the last `-` within the ceiling, not mid-word.
  const sliced = raw.slice(0, 120);
  const lastDash = sliced.lastIndexOf('-');
  return (lastDash > 60 ? sliced.slice(0, lastDash) : sliced).replace(/-+$/, '');
}

/** Calculate estimated reading time from article body copy. */
function countWords(text: string | undefined | null): number {
  if (!text) return 0;
  const trimmed = text.trim();
  if (!trimmed) return 0;
  return trimmed.split(/\s+/).length;
}

export function calculateReadingTime(article: PublicArticle): number {
  const wordsPerMinute = 200;
  let wordCount = 0;
  wordCount += countWords(article.executive_summary);
  article.sections.forEach((section) => {
    wordCount += countWords(section.content);
  });
  wordCount += countWords(article.conclusions);
  return Math.max(1, Math.ceil(wordCount / wordsPerMinute));
}

/** Infer a tier from title/summary keywords — fallback when DB value missing. */
export function inferEvidenceTier(article: PublicArticle): EvidenceTier {
  const combined = `${article.title} ${article.executive_summary}`.toLowerCase();
  if (combined.includes('meta-analysis') || combined.includes('systematic review')) {
    return 'strong';
  }
  if (
    combined.includes('randomized') ||
    combined.includes('rct') ||
    combined.includes('controlled trial')
  ) {
    return 'moderate';
  }
  return 'preliminary';
}

/** Format supplement name for display ("vitamin_d3" → "Vitamin D3"). */
export function formatSupplementName(name: string): string {
  return name
    .split(/[_-]/)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
}

// ---------------------------------------------------------------------------
// Evidence tier — v3.0 colors + labels
// ---------------------------------------------------------------------------

export interface EvidenceTierInfo {
  label: string;
  color: string;
  bgClass: string;
  dotClass: string;
}

const TIER_INFO: Record<EvidenceTier, EvidenceTierInfo> = {
  strong: {
    label: 'Strong evidence',
    color: '#16A34A',
    bgClass: 'tier tier--strong',
    dotClass: 'bg-tier-strong',
  },
  moderate: {
    label: 'Moderate evidence',
    color: '#3B82F6',
    bgClass: 'tier tier--moderate',
    dotClass: 'bg-tier-moderate',
  },
  preliminary: {
    label: 'Preliminary',
    color: '#F59E0B',
    bgClass: 'tier tier--preliminary',
    dotClass: 'bg-tier-preliminary',
  },
  insufficient: {
    label: 'Insufficient',
    color: '#9CA3AF',
    bgClass: 'tier tier--insufficient',
    dotClass: 'bg-tier-insufficient',
  },
};

export function getEvidenceTierInfo(tier: string): EvidenceTierInfo {
  return TIER_INFO[tier as EvidenceTier] ?? TIER_INFO.preliminary;
}

// ---------------------------------------------------------------------------
// Article kind — editorial categories + supplement-depth axes
// ---------------------------------------------------------------------------

export interface ArticleKindInfo {
  label: string;
  icon: string;
}

const KIND_INFO: Record<ArticleKind, ArticleKindInfo> = {
  'knowledge-base':    { label: 'Knowledge Base',    icon: '📚' },
  'evidence-dossier':  { label: 'Evidence Dossier',  icon: '📊' },
  'protocol-brief':    { label: 'Protocol Brief',    icon: '🧭' },
  mechanism:           { label: 'Mechanism of Action', icon: '⚙️' },
  dose:                { label: 'Dose & Form',       icon: '🧪' },
  safety:              { label: 'Safety & Interactions', icon: '🛡️' },
  'evidence-summary':  { label: 'Evidence Summary',  icon: '📈' },
  'biomarker-impact':  { label: 'Biomarker Impact',  icon: '🩸' },
  comparison:          { label: 'Comparison',        icon: '↔️' },
  responder:           { label: 'Responder Variability', icon: '🎯' },
  explainer:           { label: 'Explainer',         icon: '💡' },
  qa:                  { label: 'Q&A',               icon: '❓' },
};

export function getArticleKindInfo(kind: string): ArticleKindInfo {
  return KIND_INFO[kind as ArticleKind] ?? { label: 'Article', icon: '📄' };
}

/** @deprecated Use `getArticleKindInfo(article.kind)`. Kept for legacy callers. */
export const getArticleTypeInfo = getArticleKindInfo;

// ---------------------------------------------------------------------------
// Citation parsing
// ---------------------------------------------------------------------------

/**
 * Parse inline citations `[1]` and convert to `.cite-ref` anchors that link
 * to the matching card in the CitationsRail (id `c{N}`).
 *
 * The rail card carries its own "View on PubMed" action, so we keep refs
 * in-page rather than bouncing readers to an external tab mid-paragraph.
 * `:target` state on the rail card fires the flash highlight (styled in
 * globals.css via `target:` Tailwind variants on .a-cite).
 *
 * Unknown numbers pass through unchanged — preserves the raw text when a
 * source id is missing or the pipeline emits a stray number.
 */
export function parseCitations(content: string, sources: PublicArticle['sources']): string {
  return content.replace(/\[(\d+)\]/g, (match, num) => {
    const idx = sources.findIndex((s) => s.id === num);
    if (idx < 0) return match;
    const source = sources[idx];
    const railId = idx + 1; // rail cards are 1-indexed in DOM (#c1, #c2, ...)
    const titleAttr = (source.title ?? '').replace(/"/g, '&quot;');
    return `<a href="#c${railId}" class="cite-ref" title="${titleAttr}">[${num}]</a>`;
  });
}
